// positions-section.component.ts
import { Component, Input, Output, EventEmitter, OnInit, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RatingModule } from 'primeng/rating';
import { FormsModule } from '@angular/forms';
import { PipesModule } from '../../../../../../../shared/pipes/pipes.module';
import { constants } from '../../../../../../../shared/constants/constants';
import { HelperService } from '../../../../../../../shared/services/helper.service';
import { CommonBaseClass } from '../../../../../../../shared/classes/common-base-class';
import { ClientSelectionSummary } from '../../../../../../../shared/interfaces/client-portal.interface';

@Component({
  selector: 'staff-list-table',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    RatingModule,
    FormsModule,
    PipesModule
  ],
  templateUrl: './staff-list-table.component.html',
  styleUrl: './staff-list-table.component.scss'
})
export class StaffListTableComponent implements OnInit {
  @Input() positions: any[] = [];
  @Input() projectStaffList: any;
  @Input() projectDetails: any;
  @Input() user: any;
  @Input() emailType: any;
  @Input() isBackgroundCheckRequired: boolean = false;
  @Input() secureBackgroundStatus: any;
  @Input() sectionTitle: string = '';
  @Input() showSorting: boolean = true;
  @Input() tableStyleClass: string = '';
  @Input() showRoleGroupChat: boolean = false;
  @Input() isActive: boolean = false;
  @Input() firmId: string = '';
  @Input() isPWCEnabled: boolean = false;
  /** MP-5690: when true, rows get a checkbox and the header gets select-all. */
  @Input() selectable: boolean = false;
  /** MP-5690: selection lives in StaffTabComponent, keyed by position _id. */
  @Input() selectedPositionIds: ReadonlySet<string> = new Set<string>();
  /**
   * MP-5752: read-only rows for a CLIENT. The columns stay (candidate, rating, PWC
   * ranking, staff date, hourly rate, background-check result, worked-together badge);
   * the last column carries only the background-check result and nothing opens a
   * dialog, because every reviewer endpoint answers 403 for this role.
   */
  readonly clientMode = input(false);

  isCollapsed: boolean = false;

  // Events
  @Output() sortPositions = new EventEmitter<{event: any, projectStaffList: any}>();
  @Output() openReviewerDialog = new EventEmitter<string>();
  @Output() openCreateChatReviewer = new EventEmitter<any>();
  @Output() showMailDialog = new EventEmitter<any>();
  @Output() presumptiveApproveDeny = new EventEmitter<any>();
  @Output() openImmediateSendOfferModal = new EventEmitter<any>();
  @Output() showFireReviewerDialog = new EventEmitter<any>();
  @Output() openReviewerDocumentDetail = new EventEmitter<any>();
  @Output() openRoleGroupChat = new EventEmitter<any>();
  @Output() selectionChange = new EventEmitter<{ positionIds: string[]; selected: boolean }>();
  /** MP-5755: the client Select control was clicked; StaffTabComponent confirms, calls the portal and flips `clientSelected`. */
  readonly clientSelect = output<{ event: Event; position: any; roleType: string }>();


  constructor(public helperService: HelperService) { 
  }

  ngOnInit(): void {
    this.isCollapsed = this.sectionTitle === 'Denied';
  }

  toggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  // ── MP-5690 selection helpers ───────────────────────────────────────────────
  isSelected(position: any): boolean {
    return this.selectedPositionIds.has(position?._id);
  }

  /** Number of currently visible (filtered) rows in this section that are selected. */
  get selectedCount(): number {
    return this.positions.filter(p => this.selectedPositionIds.has(p._id)).length;
  }

  get allSelected(): boolean {
    return this.positions.length > 0 && this.selectedCount === this.positions.length;
  }

  get someSelected(): boolean {
    const count = this.selectedCount;
    return count > 0 && count < this.positions.length;
  }

  onRowSelectionToggle(position: any, selected: boolean): void {
    this.selectionChange.emit({ positionIds: [position._id], selected });
  }

  /** Header checkbox: selects or clears every visible row in this section. */
  onSectionSelectionToggle(selected: boolean): void {
    this.selectionChange.emit({ positionIds: this.positions.map(p => p._id), selected });
  }
  // ───────────────────────────────────────────────────────────────────────────
  onOpenRoleGroupChat(){
    this.openRoleGroupChat.emit(this.projectStaffList);
  }
  
  onSortPositions(event: any) {
    this.sortPositions.emit({event, projectStaffList: this.projectStaffList});
  }

  onOpenReviewerDialog(reviewerId: string) {
    this.openReviewerDialog.emit(reviewerId);
  }

  onOpenCreateChatReviewer(position: any) {
    this.openCreateChatReviewer.emit(position);
  }

  onShowMailDialog(projectId: string, emailType: string, firm: any, recipient: string, roleId: string, reviewerId: string) {
    this.showMailDialog.emit({projectId, emailType, firm, recipient, roleId, reviewerId});
  }

  onPresumptiveApproveDeny(event: any, position: any, roleType: string) {
    this.presumptiveApproveDeny.emit({event, position, roleType});
  }

  onOpenImmediateSendOfferModal(role: any, reviewer: any) {
    this.openImmediateSendOfferModal.emit({role, reviewer});
  }

  getReviewerTenant(reviewer: any): any {
    const byFirm = reviewer?.tenants?.find((t: any) => t.firm === this.firmId) ?? null;
    // The portal DTO is already scoped to one firm, so its single tenant entry is the right one.
    return byFirm ?? (this.clientMode() ? reviewer?.tenants?.[0] ?? null : null);
  }

  getReviewerStarRating(reviewer: any): number | string {
    return this.getReviewerTenant(reviewer)?.defaultRatings ?? reviewer?.starRating ?? '';
  }

  getReviewerRanking(reviewer: any): number | string {
    return this.getReviewerTenant(reviewer)?.ranking ?? reviewer?.ranking ?? '';
  }

  /** Background-check result as the firm template reads it; 'N/A' when the payload carries none. */
  getBackgroundResult(position: any): string {
    return position?.reviewer?.profile?.criminalCheck?.secureBackgroundResult ?? constants.secureBackgroundStatus.na;
  }

  // ── MP-5755 soft approve ────────────────────────────────────────────────────
  /**
   * The Select control shows on every row the backend accepts a select for (status OCCUPIED,
   * which covers the Staffed and Pending Approval sections) and stays on any row the group
   * already selected so it can be withdrawn after the position ends.
   */
  canClientSelect(position: any): boolean {
    return this.clientMode() && (position?.status === 'OCCUPIED' || !!position?.clientSelected);
  }


  getClientSelectTooltip(position: any): string {
    return position?.clientSelected ? 'Selected for this project' : 'Select for this project';
  }

  onClientSelect(event: Event, position: any): void {
    this.clientSelect.emit({ event, position, roleType: this.projectStaffList?.roleType });
  }

  /** FM/PM row: who picked this reviewer and when, from the firm payload's `clientSelection`. */
  getClientSelectionTooltip(position: any): string {
    const selection: ClientSelectionSummary | null = position?.clientSelection ?? null;
    if (!selection) {
      return '';
    }
    const group = selection.clientGroup?.name || 'Client';
    const user = `${selection.selectedBy?.firstName || ''} ${selection.selectedBy?.lastName || ''}`.trim();
    const date = selection.selectedAt
      ? new Date(selection.selectedAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      : '';
    return `Selected by ${group}${user ? ` (${user})` : ''}${date ? ` on ${date}` : ''}`;
  }
  // ───────────────────────────────────────────────────────────────────────────

  /** Candidate name and rate cells open the reviewer dialog for the firm; for a CLIENT they are plain text. */

  onCandidateClick(reviewerId: string): void {
    if (!this.clientMode()) {
      this.onOpenReviewerDialog(reviewerId);
    }
  }

  onShowFireReviewerDialog(event: any, reviewerId: string, positionId: string, role: any, firstName: string, lastName: string) {
    this.showFireReviewerDialog.emit({event, reviewerId, positionId, role, firstName, lastName});
  }

  onOpenReviewerDocumentDetail(reviewer: any) {
    this.openReviewerDocumentDetail.emit(reviewer);
  }

  getEndOfRoleTooltip(position: any): string {
    if (position.endedAcknowledgement) {
      if (position.endedAcknowledgeAt) {
        const readDate = new Date(position.endedAcknowledgeAt);
        return `End of role message: Read at ${readDate.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit', 
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })}`;
      }
      return 'End of role message: READ';
    }
    return 'End of role message: NOT READ';
  }

  getMessageNoOfDocumentUploadedByUser(reviewer: any): string{
    return `${Object.keys(reviewer?.docs?.filled[reviewer._id] || {}).length} of ${Object.keys(reviewer?.docs?.blank || {}).length} documents completed`
  }

  isUserCompletedOrPartiallyCompletedTheDocuments(reviewer: any): string{
    const documents = Object.keys(reviewer?.docs?.blank || {});
    const uploadedDocuments = Object.keys(reviewer?.docs?.filled[reviewer._id] || {});

    if(!uploadedDocuments.length){
      return constants.documentUploadStatus.NOT_UPLOADED;
    }

    if(documents.length > uploadedDocuments.length){
      return constants.documentUploadStatus.PARTIALLY_UPLOADED;
    }

    return constants.documentUploadStatus.UPLOADED;
  }
}